import { Check } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { PaymentIncident } from "../types/paymentIncidents.types";
import {
  INCIDENT_CASE_STEPS,
  incidentCaseStep,
  isIncidentOpen,
  isIncidentStale,
} from "../utils/paymentIncidents";

export interface IncidentCaseStripProps {
  incident: PaymentIncident;
  provider: string;
}

/**
 * The claim as a track: not reported → reported → the provider is on it → closed. The step the incident is on
 * carries how long nothing has happened, in amber once it has been a week: that is when to write again.
 */
export function IncidentCaseStrip({ incident, provider }: IncidentCaseStripProps) {
  const { t, activeLanguage } = useTranslations("paymentIncidents");
  const current = incidentCaseStep(incident.status);
  const currentIndex = current ? INCIDENT_CASE_STEPS.indexOf(current) : -1;
  const stale = isIncidentStale(incident);
  const open = isIncidentOpen(incident.status);
  const dateOf = (value: string | null) => (value ? formatDateTime(value, { locale: activeLanguage }) : null);
  const stamps: Record<(typeof INCIDENT_CASE_STEPS)[number], string | null> = {
    pending_report: dateOf(incident.created_at),
    reported: dateOf(incident.reported_at),
    in_progress: dateOf(incident.provider_responded_at),
    closed: dateOf(incident.closed_at),
  };

  return (
    <div className="space-y-2">
      <ol
        className={cn("grid grid-cols-4 gap-2", incident.status === "dismissed" && "opacity-50")}
        aria-label={t("case_strip_label")}
      >
        {INCIDENT_CASE_STEPS.map((step, index) => {
          const done = currentIndex > index || (current === "closed" && step === "closed");
          const active = index === currentIndex && current !== "closed";
          return (
            <li key={step} aria-current={active ? "step" : undefined} className="min-w-0">
              <div
                className={cn(
                  "mb-2 h-1 rounded-full",
                  done
                    ? "bg-primary"
                    : active
                      ? stale
                        ? "bg-warning"
                        : "bg-blue-500 dark:bg-blue-400"
                      : "bg-border",
                )}
              />
              <div className="flex items-center gap-1.5">
                {done && <Check className="size-3 shrink-0 text-primary" aria-hidden="true" />}
                <span
                  className={cn(
                    "truncate text-xs font-medium",
                    done || active ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {t(`case_step_${step}`, { provider })}
                </span>
              </div>
              <p className="mt-0.5 truncate text-[11px] tabular-nums text-muted-foreground">
                {stamps[step] ?? "—"}
              </p>
            </li>
          );
        })}
      </ol>
      {incident.status === "dismissed" ? (
        <p className="text-xs text-muted-foreground">{t("case_dismissed")}</p>
      ) : open ? (
        <p className={cn("text-xs", stale ? "font-medium text-warning" : "text-muted-foreground")}>
          {incident.days_since_update === 0
            ? t("case_updated_today")
            : stale
              ? t("case_stale", { count: incident.days_since_update, provider })
              : t(incident.days_since_update === 1 ? "case_quiet_one" : "case_quiet_other", {
                  count: incident.days_since_update,
                  provider,
                })}
        </p>
      ) : null}
    </div>
  );
}
